import React, { useEffect, useState } from "react";
import {
  ErrorTransactions,
  RejectedTransactions,
  ReversedTransactions,
  TransactionsComplete,
  RiskFundTransactions,
} from "../../components/charts/progressbar";
import { Helmet } from "react-helmet";
import moment from "moment";
import { NumericFormat } from "react-number-format";
import {
  exportAllTransactions,
  exportSelectedTransactions,
  fetchTransactions,
  fetchTransactionSummary,
} from "../../sdk/transactions/transactions";
import Calendar from "../../components/calendar/Calendar";
import convertDateFormat from "../../hooks/convertDate";
import TransactionModal from "../../components/Modals/transactions/TransactionModal";
import ViewReceipt from "../../components/Modals/transactions/viewReceipt";
import { capitalize } from "../../helpers/capitalize";
import ExportButtons from "../../components/export/Export";
import { downloadCSV, downloadPDF } from "../../hooks/download";
import { toast } from "react-toastify";
import { PERMISSIONS } from "../../contexts/permissions";
import { PermissionGate } from "../../helpers/PermissionGate";
import Pagination from "../../shared/Pagination";
import { TransactionRowSkeleton } from "../../skeletons/MySkeleton";

const Transactions = () => {
  const today = moment();
  const [query, setQuery] = useState("");

  const [fromDate, setFromDate] = useState(
    today?.clone()?.startOf("year")?.format("YYYY-MM-DD"),
  );

  const [toDate, setToDate] = useState(today?.format("YYYY-MM-DD"));
  const [transactionFocusedInput, setTransactionFocusedInput] = useState(null);

  const [transactions, setTransactions] = useState([]);

  const [displayedPage, setDisplayedPage] = useState(1);
  let [currentPage, setCurrentPage] = useState(1);
  let [totalPages, setTotalPages] = useState(null);
  const [totalTransactions, setTotalTransactions] = useState([]);
  const [limit, setLimit] = useState(20);

  const [type, setType] = useState("");
  const [status, setStatus] = useState("completed");
  const [leastAmount, setLeastAmount] = useState("1.00");
  const [mostAmount, setMostAmount] = useState("1000000.00");

  const [startDate, setStartDate] = useState(today?.clone()?.startOf("year"));

  const [endDate, setEndDate] = useState(today?.clone());
  const [focusedInput, setFocusedInput] = useState(null);

  const [transactionDetailsOpen, setTransactionDetailsOpen] = useState(false);
  const openTransactionDetails = () => {
    setTransactionDetailsOpen(true);
  };

  const closeTransactionDetails = () => {
    setTransactionDetailsOpen(false);
  };

  const [viewReceiptOpen, setViewReceiptOpen] = useState(false);

  const openViewReceipt = () => {
    setViewReceiptOpen(true);
    closeTransactionDetails();
  };

  const closeViewReceipt = () => {
    setViewReceiptOpen(false);
  };

  const [summaryData, setSummaryData] = useState(null);
  

  const getTransactionSummary = async () => {
    try {
      const response = await fetchTransactionSummary(
        convertDateFormat(startDate),
        convertDateFormat(endDate),
      );
      setSummaryData(response?.data || response);
    } catch (error) {
      console?.error("Failed to fetch transaction summary", error);
      setSummaryData(null);
    }
  };

  useEffect(() => {
    getTransactionSummary();
  }, [startDate, endDate]);

  useEffect(() => {
    getTransactions();
  }, [
    currentPage,
    limit,
    type,
    status,
    leastAmount,
    mostAmount,
    fromDate,
    toDate,
    query,
  ]);

  const [refreshLoading, setRefreshLoading] = useState(false);

  const handleRefetch = async () => {
    setRefreshLoading(true);
    await getTransactions();
    setRefreshLoading(false);
  };

  const [loading, setLoading] = useState(false);

  const getTransactions = async () => {
    setLoading(true);

    try {
      const response = await fetchTransactions(
        currentPage,
        limit,
        type,
        status,
        leastAmount,
        mostAmount,
        convertDateFormat(fromDate),
        convertDateFormat(toDate),
        query,
      );
      setTransactions(response.data.items);
      setDisplayedPage(response?.data?.meta?.currentPage);
      setTotalPages(response?.data?.meta?.totalPages);
      setTotalTransactions(response?.data?.meta?.totalItems);
    } catch (error) {
      console?.error("Failed to fetch transactions", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (e) => {
    setQuery(e.target.value);
  };

  const handleTypeChange = (e) => {
    setType(e.target.value);
    setCurrentPage(1);
  };

  const handleLeastInputChange = (values) => {
    setLeastAmount(values.floatValue);
  };
  const handleMostInputChange = (values) => {
    setMostAmount(values.floatValue);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setCurrentPage(1);
  };
  const handleLimitChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setLimit(newItemsPerPage);
    setCurrentPage(1);
  };

  const [singleTransaction, setSingleTransaction] = useState({});

  const handleSingleTransaction = (transaction) => {
    setSingleTransaction(transaction);
    openTransactionDetails();
  };

  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleCheckboxChange = (event, transaction) => {
    if (
      selectedTransactions.some(
        (selectedTransaction) => selectedTransaction.id === transaction.id,
      )
    ) {
      setSelectedTransactions(
        selectedTransactions.filter(
          (selectedTransaction) => selectedTransaction.id !== transaction.id,
        ),
      );
    } else {
      setSelectedTransactions([...selectedTransactions, transaction]);
    }
  };

  const handleSelectAllChange = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    setSelectedTransactions(isChecked ? transactions : []);
  };

  const handleExportAllTransactions = async (exportType) => {
    const response = await exportAllTransactions(exportType);
    exportType === "pdf"
      ? await downloadPDF(response, "transactions.pdf")
      : await downloadCSV(response, "transactions.csv");
  };

  const handleExportTransactions = async (exportType) => {
    try {
      const response = await exportSelectedTransactions(
        exportType,
        selectedTransactions,
      );
      exportType === "pdf"
        ? await downloadPDF(response, "transactions.pdf")
        : await downloadCSV(response, "transactions.csv");
      setSelectAll(false);
    } catch (error) {
      toast.error(error.response.data.message);
    }
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Belite || Transactions</title>
      </Helmet>

      <div className="w-[100%] bg-[#F5F6F7] h-full overflow-y-auto">
        <div className="my-[5px]">
          <p className="text-[#074073] text-[20px] w-[97%] mx-auto font-bold">
            TRANSACTIONS
          </p>
        </div>
        <PermissionGate required={[PERMISSIONS?.TX?.VIEW_SUMMARY]}>
          <div className="bg-white w-[98%] mx-auto rounded-[10px]">
            <div className="gap-[10px] flex items-center justify-center pt-[10px] text-[#074073]">
              Filter By Date
              <Calendar
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                focusedInput={focusedInput}
                setFocusedInput={setFocusedInput}
              />
            </div>
            <div className="flex items-center mt-[20px] px-[5px] gap-[20px] w-full">
              <div
                className="w-[25%] h-[131px] px-[5px] rounded-[5px]"
                style={{
                  borderRadius: "5px",
                  background:
                    "linear-gradient(97deg, #042159 0.34%, #00D3FF 99.66%)",
                }}
              >
                <div className="flex items-center w-full justify-between mb-[20px]">
                  <p className="text-white text-[14px]">Total Inflows</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M3.5 18.5L9.5 12.5L13.5 16.5L22 6.92L20.59 5.5L13.5 13.5L9.5 9.5L2 17L3.5 18.5Z"
                      fill="#48FF87"
                    />
                  </svg>
                </div>
                <div className="flex items-center gap-[10px] mb-[10px]">
                  <p className="text-white text-[14px]">KES.</p>
                  <p className="text-[26px] text-white font-bold">
                    {Number(
                      summaryData?.totalInflows?.amount || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
              <div
                className="w-[25%] h-[131px] px-[5px]"
                style={{
                  borderRadius: "5px",
                  background: "#999B9F",
                }}
              >
                <div className="flex items-center w-full justify-between mb-[20px]">
                  <p className="text-white text-[14px]">Loan Disbursements</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M3.5 18.5L9.5 12.5L13.5 16.5L22 6.92L20.59 5.5L13.5 13.5L9.5 9.5L2 17L3.5 18.5Z"
                      fill="#48FF87"
                    />
                  </svg>
                </div>
                <div className="flex items-center gap-[10px] mb-[10px]">
                  <p className="text-white text-[14px]">KES.</p>
                  <p className="text-[26px] text-white font-bold">
                    {Number(
                      summaryData?.loanDisbursements?.amount || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
              <div
                className="w-[25%] h-[131px] px-[5px]"
                style={{
                  borderRadius: "5px",
                  background: "#999B9F",
                }}
              >
                <div className="flex items-center w-full justify-between mb-[20px]">
                  <p className="text-white text-[14px]">Net Flow</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M3.5 18.5L9.5 12.5L13.5 16.5L22 6.92L20.59 5.5L13.5 13.5L9.5 9.5L2 17L3.5 18.5Z"
                      fill="#48FF87"
                    />
                  </svg>
                </div>
                <div className="flex items-center gap-[10px] mb-[10px]">
                  <p className="text-white text-[14px]">KES.</p>
                  <p className="text-[26px] text-white font-bold">
                    {Number(
                      summaryData?.netFlow?.amount || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center px-[5px] gap-[20px] w-[98%] py-5">
              <div className="w-1/4 h-[251px] rounded-[5px] border-[1px] px-[10px] border-[rgba(70, 111, 148, 0.41)] bg-[#F8FDFE]">
                <div className="flex items-center w-full justify-between">
                  <p className="text-[14px]">Savings Shares</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M3.5 18.5L9.5 12.5L13.5 16.5L22 6.92L20.59 5.5L13.5 13.5L9.5 9.5L2 17L3.5 18.5Z"
                      fill="#03E450"
                    />
                  </svg>
                </div>
                <div className="w-full flex my-[10px] items-center justify-center">
                  <TransactionsComplete
                    rate={summaryData?.cards?.savingsShares?.percentage}
                  />
                </div>
                <p className="text-[12px] text-[#000] text-center">Value</p>
                <div className="flex items-center justify-center gap-[5px] mb-[20px]">
                  <p className="text-[14px] text-[#074073] font-normal mt-1.5">
                    KES.
                  </p>
                  <p className="text-[20px] text-[#074073] font-[500]">
                    {Number(
                      summaryData?.cards?.savingsShares?.amount || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>

                <div className="w-full flex items-center justify-between">
                  <p className="text-[12px] text-[#000000] text-right">
                    Number of Transactions
                  </p>
                  <div>
                    <p className="text-[14px] text-[#074073]">
                      {Number(
                        summaryData?.cards?.savingsShares?.transactions ||
                          "0.00",
                      )?.toLocaleString("en-US", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                  </div>
                </div>
              </div>
              <div className="w-1/4 h-[251px] rounded-[5px] border-[1px] px-[10px] border-[rgba(70, 111, 148, 0.41)] bg-[#F8FDFE]">
                <div className="flex items-center w-full justify-between">
                  <p className="text-[14px]">Investments</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M3.5 18.5L9.5 12.5L13.5 16.5L22 6.92L20.59 5.5L13.5 13.5L9.5 9.5L2 17L3.5 18.5Z"
                      fill="#03E450"
                    />
                  </svg>
                </div>
                <div className="w-full flex my-[10px] items-center justify-center">
                  <RejectedTransactions
                    rate={summaryData?.cards?.investments?.percentage}
                  />
                </div>
                <p className="text-[12px] text-[#000] text-center">Value</p>
                <div className="flex items-center justify-center gap-[5px] mb-[20px]">
                  <p className="text-[14px] text-[#074073] font-normal mt-1.5">
                    KES.
                  </p>
                  <p className="text-[20px] text-[#074073] font-[500]">
                    {Number(
                      summaryData?.cards?.investments?.amount || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>

                <div className="w-full flex items-center justify-between">
                  {/* <div className="flex items-center justify-center gap-[5px]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      d="M20.9592 18.8744L20.9374 10.4688L12.5318 10.447C12.4442 10.4351 12.3551 10.4422 12.2705 10.4678C12.1859 10.4934 12.1078 10.5368 12.0415 10.5952C11.9752 10.6536 11.9221 10.7255 11.886 10.8062C11.8499 10.8869 11.8315 10.9743 11.8322 11.0627C11.8328 11.1511 11.8524 11.2383 11.8897 11.3184C11.9269 11.3985 11.981 11.4697 12.0482 11.5272C12.1153 11.5846 12.194 11.6269 12.279 11.6513C12.3639 11.6756 12.4531 11.6815 12.5405 11.6684L18.8393 11.6945L10.4947 20.039C10.3791 20.1547 10.3141 20.3116 10.3141 20.4752C10.3141 20.6388 10.3791 20.7958 10.4947 20.9114C10.6104 21.0271 10.7673 21.0921 10.9309 21.0921C11.0946 21.0921 11.2515 21.0271 11.3671 20.9114L19.7117 12.5669L19.7378 18.8657C19.7384 19.0294 19.804 19.1861 19.9202 19.3015C20.0363 19.4168 20.1935 19.4813 20.3572 19.4807C20.5209 19.4801 20.6777 19.4145 20.793 19.2984C20.9084 19.1822 20.9729 19.025 20.9723 18.8613L20.9592 18.8744Z"
                      fill="#03E450"
                    />
                  </svg>
                  <p className="text-[14px] text-[#074073]">-0%</p>
                </div> */}
                  <p className="text-[12px] text-[#000000]">
                    Number of Transactions
                  </p>
                  <div>
                    <p className="text-[14px] text-[#074073]">
                      {Number(
                        summaryData?.cards?.investments?.transactions || "0.00",
                      )?.toLocaleString("en-US", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                  </div>
                </div>
              </div>
              <div className="w-1/4 h-[251px] rounded-[5px] border-[1px] px-[10px] border-[rgba(70, 111, 148, 0.41)] bg-[#F8FDFE]">
                <div className="flex items-center w-full justify-between">
                  <p className="text-[14px]">Membership Fees</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M3.5 18.5L9.5 12.5L13.5 16.5L22 6.92L20.59 5.5L13.5 13.5L9.5 9.5L2 17L3.5 18.5Z"
                      fill="#03E450"
                    />
                  </svg>
                </div>
                <div className="w-full flex my-[10px] items-center justify-center">
                  <ReversedTransactions
                    rate={summaryData?.cards?.membershipSummary?.amount}
                  />
                </div>
                <p className="text-[12px] text-[#000] text-center">Value</p>
                <div className="flex items-center justify-center gap-[5px] mb-[20px]">
                  <p className="text-[14px] text-[#074073] font-normal mt-1.5">
                    KES.
                  </p>
                  <p className="text-[20px] text-[#074073] font-[500]">
                    {Number(
                      summaryData?.cards?.membershipSummary?.percentage ||
                        "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>

                <div className="w-full flex items-center justify-between">
                  <p className="text-[12px] text-[#000000]">
                    Number of Transactions
                  </p>
                  <div>
                    <p className="text-[14px] text-[#074073]">
                      {Number(
                        summaryData?.cards?.membershipSummary?.transactions ||
                          "0.00",
                      )?.toLocaleString("en-US", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="w-1/4 h-[251px] rounded-[5px] border-[1px] px-[10px] border-[rgba(70, 111, 148, 0.41)] bg-[#F8FDFE]">
                <div className="flex items-center w-full justify-between">
                  <p className="text-[14px]">Risk Fund</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M3.5 18.5L9.5 12.5L13.5 16.5L22 6.92L20.59 5.5L13.5 13.5L9.5 9.5L2 17L3.5 18.5Z"
                      fill="#03E450"
                    />
                  </svg>
                </div>
                <div className="w-full flex my-[10px] items-center justify-center">
                  <RiskFundTransactions
                    rate={summaryData?.cards?.riskFund?.percentage}
                  />
                </div>
                <p className="text-[12px] text-[#000] text-center">Value</p>
                <div className="flex items-center justify-center gap-[5px] mb-[20px]">
                  <p className="text-[14px] text-[#074073] font-normal mt-1.5">
                    KES.
                  </p>
                  <p className="text-[20px] text-[#074073] font-[500]">
                    {Number(
                      summaryData?.cards?.riskFund?.amount || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>

                <div className="w-full flex items-center justify-between">
                  <p className="text-[12px] text-[#000000]">
                    Number of Transactions
                  </p>
                  <div>
                    <p className="text-[14px] text-[#074073]">
                      {Number(
                        summaryData?.cards?.riskFund?.transactions || "0.00",
                      )?.toLocaleString("en-US", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="w-1/4 h-[251px] rounded-[5px] border-[1px] px-[10px] border-[rgba(70, 111, 148, 0.41)] bg-[#F8FDFE]">
                <div className="flex items-center w-full justify-between">
                  <p className="text-[14px]">Loan Repayments</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M3.5 18.5L9.5 12.5L13.5 16.5L22 6.92L20.59 5.5L13.5 13.5L9.5 9.5L2 17L3.5 18.5Z"
                      fill="#03E450"
                    />
                  </svg>
                </div>
                <div className="w-full flex my-[10px] items-center justify-center">
                  <ErrorTransactions
                    rate={summaryData?.cards?.loanRepayments?.percentage}
                  />
                </div>
                <p className="text-[12px] text-[#000] text-center">Value</p>
                <div className="flex items-center justify-center gap-[5px] mb-[20px]">
                  <p className="text-[14px] text-[#074073] font-normal mt-1.5">
                    KES.
                  </p>
                  <p className="text-[20px] text-[#074073] font-[500]">
                    {Number(
                      summaryData?.cards?.loanRepayments?.amount || "0.00",
                    )?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>

                <div className="w-full flex items-center justify-between">
                  <p className="text-[12px] text-[#000000]">
                    Number of Transactions
                  </p>
                  <div>
                    <p className="text-[14px] text-[#074073]">
                      {Number(
                        summaryData?.cards?.loanRepayments?.transactions ||
                          "0.00",
                      )?.toLocaleString("en-US", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PermissionGate>
        <PermissionGate required={[PERMISSIONS?.TX?.VIEW_ALL]}>
          <div className="w-[98%] mx-auto px-[5px] py-[10px] bg-white rounded-t-[10px] mt-5">
            <div className="w-[98%]">
              <p className="text-[#074073] text-[20px] my-[10px] font-bold">
                VIEW TRANSACTIONS
              </p>
              <div className="flex items-center justify-between mx-auto">
                <div className="relative items-center justify-center">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="13"
                      viewBox="0 0 14 13"
                      fill="none"
                    >
                      <circle
                        cx="5.35294"
                        cy="5.35294"
                        r="4.85294"
                        stroke="#999999"
                      />
                      <line
                        x1="9.52933"
                        y1="8.82294"
                        x2="13.3529"
                        y2="12.6465"
                        stroke="#999999"
                      />
                    </svg>
                  </span>
                  <input
                    type="text"
                    value={query}
                    onChange={handleSearch}
                    placeholder="Search transaction id, member name"
                    className="h-[35px] pl-10 border-[1px] border-[#CCCCCC] text-[14px] rounded-[2px] w-[36vw] px-[10px] focus:outline-none"
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    className="block text-white bg-[#074073] focus:outline-none focus:ring-[#074073] rounded-[3px] text-center w-[10vw] h-[40px]"
                    type="button"
                    onClick={handleRefetch}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {!refreshLoading && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="29"
                          height="29"
                          viewBox="0 0 29 29"
                          fill="none"
                        >
                          <path
                            d="M14.5931 22.9583C12.232 22.9583 10.2318 22.1395 8.59252 20.5018C6.95402 18.8633 6.13477 16.8639 6.13477 14.5036C6.13477 12.1433 6.95402 10.1431 8.59252 8.50303C10.2318 6.86211 12.232 6.04166 14.5931 6.04166C16.0294 6.04166 17.3739 6.38281 18.6265 7.06511C19.88 7.74742 20.8901 8.69999 21.657 9.92282V6.04166H22.8653V12.3612H16.5458V11.1529H21.0166C20.3875 9.9486 19.5022 8.99684 18.3607 8.29761C17.2208 7.5992 15.965 7.24999 14.5931 7.24999C12.5792 7.24999 10.8674 7.95485 9.45768 9.36457C8.04796 10.7743 7.3431 12.4861 7.3431 14.5C7.3431 16.5139 8.04796 18.2257 9.45768 19.6354C10.8674 21.0451 12.5792 21.75 14.5931 21.75C16.1438 21.75 17.5434 21.3069 18.7921 20.4208C20.0407 19.5347 20.9167 18.3667 21.4202 16.9167H22.7022C22.1698 18.7259 21.1576 20.1852 19.6657 21.2944C18.1738 22.4037 16.4829 22.9583 14.5931 22.9583Z"
                            fill="white"
                          />
                        </svg>
                      )}
                      {refreshLoading && (
                        <svg
                          aria-hidden="true"
                          className="w-[20px] h-[20px] text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                          />
                        </svg>
                      )}
                      <p className="text-[#FFF] font-normal text-[16px]">
                        REFRESH
                      </p>
                    </div>
                  </button>

                  <PermissionGate required={[PERMISSIONS?.TX?.EXPORT]}>
                    <ExportButtons
                      handleExportAllCustomers={handleExportAllTransactions}
                      handleExportCustomers={handleExportTransactions}
                      selectedCustomers={selectedTransactions}
                      setSelectedCustomers={setSelectedTransactions}
                    />
                  </PermissionGate>
                </div>
              </div>
              <div className="w-full flex items-center gap-[20px] mt-5 my-[10px]">
                <div className="border-b-[2px] border-[#8ACB8D]">
                  <p className="text-[#8ACB8D] text-[14px]">ALL TRANSACTIONS</p>
                </div>
              </div>
              <div className="h-[120px] flex items-center px-[10px] gap-[25px] w-full bg-[#F7FFD7]">
                <div className="w-[10%] flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M1.66699 4.58334C1.66699 4.25182 1.79869 3.93388 2.03311 3.69946C2.26753 3.46504 2.58547 3.33334 2.91699 3.33334H17.0837C17.4152 3.33334 17.7331 3.46504 17.9675 3.69946C18.202 3.93388 18.3337 4.25182 18.3337 4.58334C18.3337 4.91486 18.202 5.23281 17.9675 5.46723C17.7331 5.70165 17.4152 5.83334 17.0837 5.83334H2.91699C2.58547 5.83334 2.26753 5.70165 2.03311 5.46723C1.79869 5.23281 1.66699 4.91486 1.66699 4.58334ZM5.00033 9.58334C5.00033 9.25182 5.13202 8.93388 5.36644 8.69946C5.60086 8.46504 5.9188 8.33334 6.25033 8.33334H13.7503C14.0818 8.33334 14.3998 8.46504 14.6342 8.69946C14.8686 8.93388 15.0003 9.25182 15.0003 9.58334C15.0003 9.91486 14.8686 10.2328 14.6342 10.4672C14.3998 10.7016 14.0818 10.8333 13.7503 10.8333H6.25033C5.9188 10.8333 5.60086 10.7016 5.36644 10.4672C5.13202 10.2328 5.00033 9.91486 5.00033 9.58334ZM8.75033 13.3333C8.4188 13.3333 8.10086 13.465 7.86644 13.6995C7.63202 13.9339 7.50033 14.2518 7.50033 14.5833C7.50033 14.9149 7.63202 15.2328 7.86644 15.4672C8.10086 15.7016 8.4188 15.8333 8.75033 15.8333H11.2503C11.5818 15.8333 11.8998 15.7016 12.1342 15.4672C12.3686 15.2328 12.5003 14.9149 12.5003 14.5833C12.5003 14.2518 12.3686 13.9339 12.1342 13.6995C11.8998 13.465 11.5818 13.3333 11.2503 13.3333H8.75033Z"
                      fill="black"
                    />
                  </svg>
                </div>
                <div className="w-[90%] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <p className="!text-[12px]">DATE</p>
                    <Calendar
                      startDate={fromDate}
                      endDate={toDate}
                      setStartDate={setFromDate}
                      setEndDate={setToDate}
                      focusedInput={transactionFocusedInput}
                      setFocusedInput={setTransactionFocusedInput}
                    />
                  </div>
                  <div className="flex flex-col gap-[10px] items-end">
                    <div className="flex items-center gap-[5px]">
                      <p className="!text-[12px]">TYPE</p>
                      <select
                        name="type"
                        id="type"
                        className="w-[190px] border border-gray-300 h-[30px] text-[14px] px-[10px] focus:outline-none"
                        onChange={handleTypeChange}
                        value={type}
                      >
                        <option value="">All</option>
                        {/* <option value="send">Debit</option>
                        <option value="receive">Credit</option> */}
                        <option value="loan">Loans Disbursement</option>
                        <option value="loan_processing_fee">
                          Loan Processing Fee
                        </option>
                        <option value="loan_repayment">Loan Repayment</option>
                        <option value="membership">Membership Fee</option>
                        <option value="reversal">Reversal</option>
                        <option value="deposit">Savings Contribution</option>
                        <option value="shares">Shares Contribution</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-[5px]">
                      <p className="!text-[12px]">LEAST AMOUNT</p>
                      <NumericFormat
                        thousandSeparator={true}
                        decimalScale={2}
                        fixedDecimalScale
                        prefix={"KES "}
                        onValueChange={handleLeastInputChange}
                        value={leastAmount}
                        className="border border-gray-300"
                        style={{
                          height: "30px",
                          paddingRight: "10px",
                          paddingLeft: "10px",
                          width: "190px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-[10px] items-end">
                    <div className="flex items-center gap-[5px]">
                      <p className="!text-[12px]">HIGHEST AMOUNT</p>
                      <NumericFormat
                        thousandSeparator={true}
                        decimalScale={2}
                        fixedDecimalScale
                        prefix={"KES "}
                        onValueChange={handleMostInputChange}
                        value={mostAmount}
                        className="border border-gray-300"
                        style={{
                          height: "30px",
                          paddingRight: "10px",
                          paddingLeft: "10px",
                          width: "160px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-[5px]">
                      <p className="!text-[12px]">STATUS</p>
                      <select
                        name="status"
                        id="status"
                        className="w-[160px] border border-gray-300 h-[30px] text-[14px] px-[10px] focus:outline-none"
                        value={status}
                        onChange={handleStatusChange}
                      >
                        <option value="completed">Completed</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-[44px] bg-[#F0FFFE] w-full flex items-center mt-[2px]">
                <input
                  type="checkbox"
                  name="transaction"
                  id="transaction"
                  className="w-[10%]"
                  checked={selectAll}
                  onChange={handleSelectAllChange}
                />
                <p className="w-[14%] text-[14px] text-[#000000]">
                  TRANSACTION ID
                </p>
                <p className="w-[16%] text-[14px] text-[#000000]">MEMBER</p>
                <p className="w-[12%] text-[14px] text-[#000000]">DATE</p>
                <p className="w-[22%] text-[14px] text-[#000000]">
                  REFERENCE NO.
                </p>
                <p className="w-[16%] text-[14px] text-[#000000]">TYPE</p>
                <p className="w-[13%] text-[14px] text-[#000000]">
                  PAYMENT MODE
                </p>
                <p className="w-[13%] text-[14px] text-[#000000]">AMOUNT</p>
                <div className="w-[16.5%] flex items-center gap-[5px]">
                  <p className=" text-[14px] text-[#000000]">STATUS</p>
                </div>
              </div>
              <div>
                {loading &&
                  Array?.from({ length: 6 })?.map((_, index) => (
                    <TransactionRowSkeleton key={index} />
                  ))}
                {!loading &&
                  transactions?.length > 0 &&
                  transactions?.map((transaction) => (
                    <div
                      key={transaction?.id}
                      className="h-[44px] !text-[14px] bg-[#fff] w-full flex items-center"
                    >
                      <input
                        type="checkbox"
                        name="transaction"
                        id="transaction"
                        className="w-[10%]"
                        onChange={(event) =>
                          handleCheckboxChange(event, transaction)
                        }
                        checked={selectedTransactions?.some(
                          (selectedTransaction) =>
                            selectedTransaction?.id === transaction?.id,
                        )}
                      />
                      <PermissionGate
                        required={[PERMISSIONS?.TX?.VIEW_TRANSACTION_DETAILS]}
                      >
                        <p
                          onClick={() => handleSingleTransaction(transaction)}
                          className="w-[14%] wrap text-[#037BD1] cursor-pointer !text-[14px]"
                        >
                          {transaction?.public_id}
                        </p>
                      </PermissionGate>
                      <PermissionGate
                        required={[PERMISSIONS?.TX?.VIEW_TRANSACTION_DETAILS]}
                        fallback={
                          <p className="w-[14%] wrap text-[#037BD1] cursor-not-allowed !text-[14px]">
                            {transaction?.public_id}
                          </p>
                        }
                      />
                      <p className="w-[16%] !text-[14px] text-[#000000]">
                        {transaction?.type === "receive"
                          ? transaction?.receiver_name
                              ?.split(" ")
                              ?.map(
                                (name) =>
                                  name.charAt(0).toUpperCase() +
                                  name.slice(1).toLowerCase(),
                              )
                              ?.join(" ")
                          : transaction?.receiver_name
                              ?.split(" ")
                              ?.map(
                                (name) =>
                                  name?.charAt(0)?.toUpperCase() +
                                  name?.slice(1)?.toLowerCase(),
                              )
                              ?.join(" ")}
                      </p>
                      <p className="w-[12%] !text-[14px] text-[#000000]">
                        {
                          new Date(transaction?.createdAt)
                            ?.toISOString()
                            ?.split("T")[0]
                        }
                      </p>
                      <p className="w-[22%] !text-[14px] text-[#000000]">
                        {transaction?.ref_number}
                      </p>
                      <p className="w-[16%] !text-[14px] text-[#000000]">
                        {capitalize(transaction?.type?.replace(/_/g, " "))}
                      </p>
                      <p className="w-[13%] !text-[14px] text-[#000000]">
                        {transaction?.deposit_method}
                      </p>
                      {transaction?.type === "receive" ? (
                        <p className="w-[13%] !text-[14px] text-[#000000]">
                          + KES &nbsp;
                          {Number(
                            transaction?.amount || "0.00",
                          )?.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      ) : (
                        <p className="w-[13%] !text-[14px] text-[#000000]">
                          - KES &nbsp;
                          {Number(
                            transaction?.amount || "0.00",
                          )?.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      )}
                      <div className="flex items-center gap-[5px] w-[16.5%]">
                        {transaction?.status === "completed" && (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 0C8.73438 0 9.44271 0.09375 10.125 0.28125C10.8073 0.46875 11.4427 0.739583 12.0312 1.09375C12.6198 1.44792 13.1589 1.86458 13.6484 2.34375C14.138 2.82292 14.5573 3.36198 14.9062 3.96094C15.2552 4.5599 15.5234 5.19792 15.7109 5.875C15.8984 6.55208 15.9948 7.26042 16 8C16 8.73438 15.9062 9.44271 15.7188 10.125C15.5312 10.8073 15.2604 11.4427 14.9062 12.0312C14.5521 12.6198 14.1354 13.1589 13.6562 13.6484C13.1771 14.138 12.638 14.5573 12.0391 14.9062C11.4401 15.2552 10.8021 15.5234 10.125 15.7109C9.44792 15.8984 8.73958 15.9948 8 16C7.26562 16 6.55729 15.9062 5.875 15.7188C5.19271 15.5312 4.55729 15.2604 3.96875 14.9062C3.38021 14.5521 2.84115 14.1354 2.35156 13.6562C1.86198 13.1771 1.44271 12.638 1.09375 12.0391C0.744792 11.4401 0.476562 10.8021 0.289062 10.125C0.101562 9.44792 0.00520833 8.73958 0 8C0 7.26562 0.09375 6.55729 0.28125 5.875C0.46875 5.19271 0.739583 4.55729 1.09375 3.96875C1.44792 3.38021 1.86458 2.84115 2.34375 2.35156C2.82292 1.86198 3.36198 1.44271 3.96094 1.09375C4.5599 0.744792 5.19792 0.476562 5.875 0.289062C6.55208 0.101562 7.26042 0.00520833 8 0ZM12.7109 5.35156L11.6484 4.28906L6.5 9.4375L4.35156 7.28906L3.28906 8.35156L6.5 11.5625L12.7109 5.35156Z"
                              fill="#0C722F"
                            />
                          </svg>
                        )}
                        {transaction?.status === "failed" && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <g clip-path="url(#clip0_8496_1645)">
                              <path
                                d="M6 0C6.55078 0 7.08203 0.0703125 7.59375 0.210938C8.10547 0.351562 8.58203 0.554688 9.02344 0.820312C9.46484 1.08594 9.86914 1.39844 10.2363 1.75781C10.6035 2.11719 10.918 2.52148 11.1797 2.9707C11.4414 3.41992 11.6426 3.89844 11.7832 4.40625C11.9238 4.91406 11.9961 5.44531 12 6C12 6.55078 11.9297 7.08203 11.7891 7.59375C11.6484 8.10547 11.4453 8.58203 11.1797 9.02344C10.9141 9.46484 10.6016 9.86914 10.2422 10.2363C9.88281 10.6035 9.47852 10.918 9.0293 11.1797C8.58008 11.4414 8.10156 11.6426 7.59375 11.7832C7.08594 11.9238 6.55469 11.9961 6 12C5.44922 12 4.91797 11.9297 4.40625 11.7891C3.89453 11.6484 3.41797 11.4453 2.97656 11.1797C2.53516 10.9141 2.13086 10.6016 1.76367 10.2422C1.39648 9.88281 1.08203 9.47852 0.820312 9.0293C0.558594 8.58008 0.357422 8.10156 0.216797 7.59375C0.0761719 7.08594 0.00390625 6.55469 0 6C0 5.44922 0.0703125 4.91797 0.210938 4.40625C0.351562 3.89453 0.554688 3.41797 0.820312 2.97656C1.08594 2.53516 1.39844 2.13086 1.75781 1.76367C2.11719 1.39648 2.52148 1.08203 2.9707 0.820312C3.41992 0.558594 3.89844 0.357422 4.40625 0.216797C4.91406 0.0761719 5.44531 0.00390625 6 0ZM9.5332 4.01367L8.73633 3.2168L4.875 7.07812L3.26367 5.4668L2.4668 6.26367L4.875 8.67188L9.5332 4.01367Z"
                                fill="#0C722F"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_8496_1645">
                                <rect width="12" height="12" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        )}
                        {transaction?.status === "cancelled" && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 19 20"
                            fill="none"
                          >
                            <path
                              d="M9.5 10.8906V6.14062V10.8906ZM17.2188 10C17.2188 12.0471 16.4055 14.0104 14.958 15.458C13.5104 16.9055 11.5471 17.7188 9.5 17.7188C7.45286 17.7188 5.48957 16.9055 4.04202 15.458C2.59447 14.0104 1.78125 12.0471 1.78125 10C1.78125 7.95286 2.59447 5.98957 4.04202 4.54202C5.48957 3.09447 7.45286 2.28125 9.5 2.28125C11.5471 2.28125 13.5104 3.09447 14.958 4.54202C16.4055 5.98957 17.2188 7.95286 17.2188 10Z"
                              fill="#FAB32B"
                            />
                            <path
                              d="M9.5 10.8906V6.14062M17.2188 10C17.2187 12.0471 16.4055 14.0104 14.958 15.458C13.5104 16.9055 11.5471 17.7188 9.5 17.7188C7.45286 17.7188 5.48957 16.9055 4.04202 15.458C2.59447 14.0104 1.78125 12.0471 1.78125 10C1.78125 7.95286 2.59447 5.98957 4.04202 4.54202C5.48957 3.09447 7.45286 2.28125 9.5 2.28125C11.5471 2.28125 13.5104 3.09447 14.958 4.54202C16.4055 5.98957 17.2188 7.95286 17.2188 10Z"
                              stroke="white"
                              stroke-width="1.3"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M10.0938 13.5625C10.0938 13.72 10.0312 13.871 9.91985 13.9823C9.8085 14.0937 9.65747 14.1562 9.5 14.1562C9.34253 14.1562 9.19151 14.0937 9.08016 13.9823C8.96881 13.871 8.90625 13.72 8.90625 13.5625C8.90625 13.405 8.96881 13.254 9.08016 13.1427C9.19151 13.0313 9.34253 12.9688 9.5 12.9688C9.65747 12.9688 9.8085 13.0313 9.91985 13.1427C10.0312 13.254 10.0938 13.405 10.0938 13.5625Z"
                              fill="#FAB32B"
                              stroke="white"
                            />
                          </svg>
                        )}
                        {transaction?.status === "pending" && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 15 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M3.75 8.625C4.0625 8.625 4.32825 8.5155 4.54725 8.2965C4.76625 8.0775 4.8755 7.812 4.875 7.5C4.875 7.1875 4.7655 6.92175 4.5465 6.70275C4.3275 6.48375 4.062 6.3745 3.75 6.375C3.4375 6.375 3.17175 6.4845 2.95275 6.7035C2.73375 6.9225 2.6245 7.188 2.625 7.5C2.625 7.8125 2.7345 8.07825 2.9535 8.29725C3.1725 8.51625 3.438 8.6255 3.75 8.625ZM7.5 8.625C7.8125 8.625 8.07825 8.5155 8.29725 8.2965C8.51625 8.0775 8.6255 7.812 8.625 7.5C8.625 7.1875 8.5155 6.92175 8.2965 6.70275C8.0775 6.48375 7.812 6.3745 7.5 6.375C7.1875 6.375 6.92175 6.4845 6.70275 6.7035C6.48375 6.9225 6.3745 7.188 6.375 7.5C6.375 7.8125 6.4845 8.07825 6.7035 8.29725C6.9225 8.51625 7.188 8.6255 7.5 8.625ZM11.25 8.625C11.5625 8.625 11.8282 8.5155 12.0472 8.2965C12.2662 8.0775 12.3755 7.812 12.375 7.5C12.375 7.1875 12.2655 6.92175 12.0465 6.70275C11.8275 6.48375 11.562 6.3745 11.25 6.375C10.9375 6.375 10.6717 6.4845 10.4527 6.7035C10.2337 6.9225 10.1245 7.188 10.125 7.5C10.125 7.8125 10.2345 8.07825 10.4535 8.29725C10.6725 8.51625 10.938 8.6255 11.25 8.625ZM7.5 15C6.4625 15 5.4875 14.803 4.575 14.409C3.6625 14.015 2.86875 13.4807 2.19375 12.8062C1.51875 12.1312 0.9845 11.3375 0.591 10.425C0.1975 9.5125 0.0005 8.5375 0 7.5C0 6.4625 0.197 5.4875 0.591 4.575C0.985 3.6625 1.51925 2.86875 2.19375 2.19375C2.86875 1.51875 3.6625 0.9845 4.575 0.591C5.4875 0.1975 6.4625 0.0005 7.5 0C8.5375 0 9.5125 0.197 10.425 0.591C11.3375 0.985 12.1312 1.51925 12.8062 2.19375C13.4812 2.86875 14.0157 3.6625 14.4097 4.575C14.8037 5.4875 15.0005 6.4625 15 7.5C15 8.5375 14.803 9.5125 14.409 10.425C14.015 11.3375 13.4807 12.1312 12.8062 12.8062C12.1312 13.4812 11.3375 14.0157 10.425 14.4097C9.5125 14.8037 8.5375 15.0005 7.5 15Z"
                              fill="#554D4D"
                            />
                          </svg>
                        )}
                        <p
                          className={`text-[14px] ${
                            transaction?.status === "pending"
                              ? "text-[#000000]"
                              : transaction?.status === "completed"
                                ? "text-[#0C722F]"
                                : "text-[#074073]"
                          }`}
                        >
                          {capitalize(transaction?.status)}
                        </p>
                      </div>
                    </div>
                  ))}
                {!loading && transactions?.length === 0 && (
                  <div className="px-[20px] flex items-center mt-[20px]">
                    <p className="text-[#074073] !text-[14px]">
                      No Record of Transaction
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="h-[70px] bg-white w-[97%] px-[20px] mx-auto mb-[20px] flex items-center justify-between">
              <div className="flex items-center gap-[20px]">
                <p className="text-[#074073] !text-[14px]">Rows per Page</p>
                <select
                  name="page"
                  id="page"
                  className="w-[71px] border-[1px] px-[5px] h-[30px] focus:outline-none"
                  value={limit}
                  onChange={handleLimitChange}
                >
                  <option value="50">50</option>
                  <option value="30">30</option>
                  <option value="20">20</option>
                  <option value="10">10</option>
                </select>
                <p className="text-[#074073] text-[14px]">
                  {displayedPage} / {totalPages} ({totalTransactions} records)
                </p>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </PermissionGate>
      </div>

      <TransactionModal
        transactionDetailsOpen={transactionDetailsOpen}
        closeTransactionDetails={closeTransactionDetails}
        singleTransaction={singleTransaction}
        openViewReceipt={openViewReceipt}
      />
      <ViewReceipt
        viewReceiptOpen={viewReceiptOpen}
        closeViewReceipt={closeViewReceipt}
        singleTransaction={singleTransaction}
      />
    </>
  );
};

export default Transactions;
